import React, { useContext } from "react";
import { QueryKey, useInfiniteQuery } from "react-query";
import { CellClickedEvent } from "@ag-grid-community/core";

import { useHistory, useRouteMatch } from "react-router-dom";

import UserContext from "contexts/userContext";
import TransactionsDataGrid from "components/TransactionsDataGrid";

import useTransactionsRequestParams from "hooks/useTransactionsRequestParams";

import { hasPermission } from "fe-shared/src/helpers/permissions";
import { PermissionLevel } from "fe-shared/src/types/domain";
import { getTransactionsFilterParams } from "fe-shared-transactions/src/helpers/queryString";
import useQueryFilters from "fe-shared-transactions/src/hooks/useQueryFilters";
import { TRANSACTIONS_QUERY_KEY, PAGE_SIZE, PERMISSIONS } from "consts";

import * as api from "./modules/api";

interface Props {
  lastOpenedNumber: string | null;
  clearLastOpenedNumber: () => void;
}

const Transactions = ({ lastOpenedNumber, clearLastOpenedNumber }: Props) => {
  const history = useHistory();
  const match = useRouteMatch();
  const user = useContext(UserContext);
  const {
    requestParams,
    setFilters,
    ...otherProps
  } = useTransactionsRequestParams();

  useQueryFilters(setFilters, getTransactionsFilterParams);

  const canViewTransactions = hasPermission(user?.permissions, {
    name: PERMISSIONS.transactionsList,
    level: PermissionLevel.Read,
  });
  const canViewDetails = hasPermission(user?.permissions, {
    name: PERMISSIONS.transactionDetails,
    level: PermissionLevel.Read,
  });

  const requestsQueryKey: QueryKey = [TRANSACTIONS_QUERY_KEY, requestParams];

  const transactions = useInfiniteQuery(requestsQueryKey, api.getTransactions, {
    enabled: canViewTransactions,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    getFetchMore: (lastGroup, allGroups) =>
      allGroups.length * PAGE_SIZE < lastGroup.total
        ? allGroups.length + 1
        : undefined,
  });

  const onCellClicked = (e: CellClickedEvent) => {
    const eventTags = ["BUTTON", "A"];
    const currentNode = (e.event?.target as Element).nodeName;
    const parentNode = (e.event?.target as Element).parentNode?.nodeName || "";
    const isCurrentTagClicked = eventTags.includes(currentNode);
    const isParentTagClicked = eventTags.includes(parentNode);
    const colId = e.column.getColId();

    if (
      canViewDetails &&
      !isCurrentTagClicked &&
      !isParentTagClicked &&
      colId !== "assignee"
    ) {
      history.push(`${match.url}/${e.data.transactionNumber}?navigation=true`);
    }
  };

  return canViewTransactions ? (
    <TransactionsDataGrid
      transactions={transactions}
      lastOpenedNumber={lastOpenedNumber}
      clearLastOpenedNumber={clearLastOpenedNumber}
      onCellClicked={onCellClicked}
      onCellMouseDown={(e) => {
        const colId = e.column.getColId();

        if (colId === "assignee") {
          e.api.ensureColumnVisible(colId);
        }
      }}
      setFilters={setFilters}
      {...otherProps}
    />
  ) : null;
};

export default Transactions;
